import type { StaffCustomerCoverage } from "@lafayette/schemas";
import { useQueryClient } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { useState } from "react";
import { useParams } from "react-router";
import { useWideLayout } from "../../layout/shell-layout";
import { AreaLink } from "../../navigation/AreaLink";
import { deepLinks } from "../../navigation/deep-links";
import { areaPath } from "../../navigation/offering";
import { AsyncState } from "../../ui/async-state";
import { Badge, pillClass } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { DataTable } from "../../ui/data-table";
import { Icon } from "../../ui/icon";
import { BackLink } from "../../ui/links";
import { Notice } from "../../ui/notice";
import { PageHeader } from "../../ui/page-header";
import { StatusMessage } from "../../ui/status-message";
import { useTeamCapabilities } from "../access";
import {
  AddBackupDialog,
  AddTemporaryDialog,
  AssignPrimaryDialog,
  EndAssignmentDialog,
  ReplacePrimaryDialog,
} from "../components/CoverageDialogs";
import { Muted } from "../components/parts";
import { describeTeamError } from "../errors";
import { utcOrOpen, utcText } from "../format";
import {
  assignmentTypeHints,
  assignmentTypeLabels,
  availabilityReasonLabels,
  conversationModeLabels,
} from "../labels";
import { teamKeys, useCustomerCoverage } from "../queries";

// The cover of one customer (CHT-768; supervisors): who is primary, who is backup, who covers for a while. Exactly one primary is true at all
// times: the API refuses a second one and a replacement is one command. Every command carries the ids of the assignments this screen showed, so
// a supervisor who works from an old screen is refused and shown the latest cover (nothing is written). A TEMPORARY row whose window has run out
// still reads active on the server until a command ends it: it holds no slot and nobody (the rules ignore it), so the screen shows it as ended,
// offers no "End" for it, lets a new temporary cover be added, and still sends its id in the "what I saw" check (CHT-768 review fix 4).

type Row = StaffCustomerCoverage["assignments"][number];
type DialogState =
  | Readonly<{ kind: "primary" | "replace" | "backup" | "temporary" }>
  | Readonly<{ kind: "end"; assignment: Row }>
  | null;

type AssignmentView = Readonly<{ row: Row; onEnd: () => void }>;

// The column list is a module constant: `DataTable` draws a cell with `flexRender`, which treats a cell function as a component.
const assignmentColumn = createColumnHelper<AssignmentView>();
// No `rowHeader` column here: the type of cover repeats (a customer can have several backups and several temporary windows), and one person can
// hold more than one row, so no cell of a row names that row alone.
const assignmentColumns = [
  assignmentColumn.display({
    id: "cover",
    header: "Cover",
    cell: ({ row }) => (
      <span className="font-semibold" title={assignmentTypeHints[row.original.row.type]}>
        {assignmentTypeLabels[row.original.row.type]}
      </span>
    ),
  }),
  assignmentColumn.display({
    id: "person",
    header: "Person",
    cell: ({ row }) => (
      <span className="font-serif text-lg font-medium compact:text-base">
        {row.original.row.staff.displayName}
      </span>
    ),
  }),
  assignmentColumn.display({
    id: "from",
    header: "From",
    cell: ({ row }) => utcText(row.original.row.startsAt),
  }),
  assignmentColumn.display({
    id: "until",
    header: "Until",
    cell: ({ row }) => utcOrOpen(row.original.row.endsAt),
  }),
  assignmentColumn.display({
    id: "state",
    header: "State",
    cell: ({ row }) =>
      row.original.row.elapsed ? (
        <Badge tone="neutral" className={pillClass}>
          Window ended
        </Badge>
      ) : row.original.row.effective ? (
        <Badge tone="success" className={pillClass}>
          In force
        </Badge>
      ) : (
        <Badge tone="info" className={pillClass}>
          Starts later
        </Badge>
      ),
  }),
  assignmentColumn.display({
    id: "actions",
    header: "Actions",
    cell: ({ row }) =>
      row.original.row.elapsed ? (
        <Muted>Cleared on the next change.</Muted>
      ) : (
        <Button type="button" variant="danger" size="sm" onClick={row.original.onEnd}>
          End{" "}
          <span className="sr-only">
            {assignmentTypeLabels[row.original.row.type].toLowerCase()} cover of{" "}
            {row.original.row.staff.displayName}
          </span>
        </Button>
      ),
  }),
];

/**
 * The route element. The view is keyed by the customer, so the notice, the open dialog and the cached read of one customer never carry over
 * to the next customer when the address changes (found in the browser walk: a "primary assigned" notice stayed on another customer's page).
 */
export function CustomerCoveragePage() {
  const { customerId = "" } = useParams();
  return <CustomerCoverageView key={customerId} customerId={customerId} />;
}

function CustomerCoverageView({ customerId }: Readonly<{ customerId: string }>) {
  useWideLayout();
  const { capabilities, isLoading: rolesLoading } = useTeamCapabilities();
  const coverage = useCustomerCoverage(
    customerId,
    capabilities.manageCoverage && customerId !== "",
  );
  const client = useQueryClient();
  const [dialog, setDialog] = useState<DialogState>(null);
  const [notice, setNotice] = useState<string | null>(null);

  if (rolesLoading) {
    return (
      <AsyncState kind="loading" title="Checking your access" message="Reading your staff role." />
    );
  }
  if (!capabilities.manageCoverage) {
    return (
      <AsyncState
        kind="error"
        title="Not available for your role"
        message="Only a supervisor manages the cover of customers."
      />
    );
  }

  const reload = () => {
    void client.invalidateQueries({ queryKey: teamKeys.coverage(customerId) });
    void client.invalidateQueries({ queryKey: teamKeys.gaps });
    void client.invalidateQueries({ queryKey: teamKeys.team });
  };
  const finish = (message: string) => {
    setDialog(null);
    setNotice(message);
    reload();
  };

  return (
    <section className="grid grid-cols-[minmax(0,1fr)] gap-6" aria-labelledby="page-title">
      <BackLink to={areaPath("coverage")}>Back to coverage</BackLink>
      {coverage.isPending ? (
        <AsyncState kind="loading" title="Loading the cover" message="Waiting for the answer." />
      ) : coverage.isError ? (
        <AsyncState
          kind="error"
          title="The cover could not be loaded"
          message={describeTeamError(coverage.error).message}
          onRetry={() => void coverage.refetch()}
        />
      ) : (
        <Loaded
          data={coverage.data}
          busy={coverage.isFetching}
          notice={notice}
          dialog={dialog}
          setDialog={setDialog}
          reload={reload}
          finish={finish}
        />
      )}
    </section>
  );
}

function Loaded({
  data,
  busy,
  notice,
  dialog,
  setDialog,
  reload,
  finish,
}: Readonly<{
  data: StaffCustomerCoverage;
  busy: boolean;
  notice: string | null;
  dialog: DialogState;
  setDialog: (value: DialogState) => void;
  reload: () => void;
  finish: (message: string) => void;
}>) {
  // The snapshot holds EVERY row the server lists, an elapsed one too; the slots and the people are those of the rows still in force.
  const seen = data.assignments.map((row) => row.assignmentId);
  const live = data.assignments.filter((row) => !row.elapsed);
  const taken = new Set(live.map((row) => row.staff.staffUserId));
  const has = (type: Row["type"]) => live.some((row) => row.type === type);
  // A primary may be a person who is the backup or the temporary cover now: the command promotes them in one write. Only the current primary
  // cannot be picked again (CHT-768 review fix 5).
  const primaryTaken = new Set(
    live.filter((row) => row.type === "PRIMARY").map((row) => row.staff.staffUserId),
  );
  const holding = new Map(
    live
      .filter((row) => row.type !== "PRIMARY")
      .map((row) => [row.staff.staffUserId, assignmentTypeLabels[row.type].toLowerCase()] as const),
  );
  const common = {
    customer: data.customer,
    seen,
    candidates: data.candidates,
    taken,
    holding,
    onDone: finish,
    onReload: reload,
    onClose: () => setDialog(null),
  };

  const views: AssignmentView[] = data.assignments.map((row) => ({
    row,
    onEnd: () => setDialog({ kind: "end", assignment: row }),
  }));

  return (
    <>
      <PageHeader
        eyebrow="Cover"
        title={data.customer.displayName}
        titleId="page-title"
        description={
          <>
            Who takes this customer's work.{" "}
            <AreaLink
              area="customers"
              className="text-link underline underline-offset-4 hover:no-underline"
              to={deepLinks.customerSupport(data.customer.id)}
            >
              Open the support view
            </AreaLink>
            {data.conversation === null ? null : (
              <>
                {" · "}
                <AreaLink
                  area="concierge"
                  className="text-link underline underline-offset-4 hover:no-underline"
                  to={deepLinks.conversation(data.conversation.id)}
                >
                  Open the conversation
                </AreaLink>{" "}
                <Muted>({conversationModeLabels[data.conversation.mode] ?? "Open"})</Muted>
              </>
            )}
          </>
        }
      />

      {notice === null ? null : <StatusMessage message={{ tone: "success", text: notice }} />}

      {data.hasEffectivePrimary ? null : (
        <Notice tone="warning" role="status" className="max-w-3xl">
          <strong>No primary concierge.</strong> Work for this customer goes to the backup, then to
          the supervisor queue. Assign a primary.
        </Notice>
      )}

      <section aria-labelledby="cover-title" aria-busy={busy} className="grid gap-3">
        <h2 id="cover-title" className="m-0 font-serif text-[26px] leading-8 font-medium">
          Who covers
        </h2>
        {data.assignments.length === 0 ? (
          <AsyncState
            kind="empty"
            title="Nobody covers this customer"
            message="Assign a primary concierge."
          />
        ) : (
          <DataTable<AssignmentView>
            caption="Active cover assignments of this customer"
            data={views}
            getRowId={(view) => view.row.assignmentId}
            columns={assignmentColumns}
            banded="strong"
          />
        )}
        <div className="flex flex-wrap gap-2">
          {has("PRIMARY") ? (
            <Button type="button" variant="outline" onClick={() => setDialog({ kind: "replace" })}>
              Replace primary
            </Button>
          ) : (
            <Button type="button" variant="outline" onClick={() => setDialog({ kind: "primary" })}>
              <Icon name="user-add-02" />
              Assign primary
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            onClick={() => setDialog({ kind: "backup" })}
            disabled={has("BACKUP")}
            title={has("BACKUP") ? "This customer already has a backup." : undefined}
          >
            Add backup
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => setDialog({ kind: "temporary" })}
            disabled={has("TEMPORARY")}
            title={has("TEMPORARY") ? "This customer already has temporary cover." : undefined}
          >
            Add temporary cover
          </Button>
        </div>
      </section>

      <section aria-labelledby="away-title">
        <Card>
          <CardHeader>
            <CardTitle id="away-title" className="text-2xl leading-[30px]">
              Who is away
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.unavailability.length === 0 ? (
              <Muted>Nobody who covers this customer has an open unavailability window.</Muted>
            ) : (
              <ul className="m-0 grid list-disc gap-1 pl-5 text-sm leading-6">
                {data.unavailability.map((window) => (
                  <li key={window.windowId}>
                    {window.staff.displayName}: {availabilityReasonLabels[window.reasonCode]},{" "}
                    {utcText(window.startsAt)} to {utcText(window.endsAt)}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </section>

      {dialog?.kind === "primary" ? <AssignPrimaryDialog {...common} taken={primaryTaken} /> : null}
      {dialog?.kind === "replace" ? (
        <ReplacePrimaryDialog {...common} taken={primaryTaken} />
      ) : null}
      {dialog?.kind === "backup" ? <AddBackupDialog {...common} /> : null}
      {dialog?.kind === "temporary" ? <AddTemporaryDialog {...common} /> : null}
      {dialog?.kind === "end" ? (
        <EndAssignmentDialog
          customer={data.customer}
          seen={seen}
          assignment={dialog.assignment}
          onDone={finish}
          onReload={reload}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </>
  );
}
